/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parse, format, isValid } from 'date-fns';
import TextField from '@mui/material/TextField';
import * as styles from '../offices.styles';
import OfficeFormType from '../enums/office-form-types';
import type Office from '../models/office';
import { ApplyValueToOffice } from '../office.component';
import { getCateringLunchPreview, sendCateringTestEmail, type CateringLunchPreviewRow, updateOffice } from '../services/offices.service';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../../core/components/center/center.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment } from '../../../core/components/flex/flex.component';
import notify from '../../../core/components/notify/notify.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import CommonStyles from '../../../core/common.styles';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import AppConstants from '../../../core/app.constants';
import TimezoneSelector, { getTzOffsetMinutes } from '../components/timezone-selector.component';

/**
 * Converts a local HH:mm time in the given IANA timezone to UTC HH:mm.
 * Uses the current UTC offset of the timezone, which correctly accounts for DST.
 * Returns an empty string when the input is blank or malformed.
 */
function localToUtcHHmm(localHHmm: string, tz: string): string {
  const parts = localHHmm.split(':');
  if (parts.length !== 2) return '';
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return '';
  const offsetMinutes = getTzOffsetMinutes(tz, new Date());
  const localMinutes = hh * 60 + mm;
  // Wrap around midnight so result stays in [0, 1439]
  const rawUtc = (localMinutes - offsetMinutes) % 1440;
  const utcMinutes = (rawUtc + 1440) % 1440;
  return `${String(Math.floor(utcMinutes / 60)).padStart(2, '0')}:${String(utcMinutes % 60).padStart(2, '0')}`;
}

interface Props {
  readonly officeId: string | undefined;
  readonly office: Office | undefined;
}

const OfficeCateringGroup = ({ officeId, office }: Props): React.JSX.Element => {
  const keycloak = useKeycloak();
  const [isSaving, setIsSaving] = useState(false);
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [officeToUpdate, setOfficeToUpdate] = useState<Office | undefined>(undefined);
  const [previewRows, setPreviewRows] = useState<CateringLunchPreviewRow[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(true);

  const planPeriodDays = useMemo(() => office?.planPeriodDays ?? 14, [office?.planPeriodDays]);

  const previewEmailBody = useMemo((): string => {
    const body = officeToUpdate?.cateringNotificationEmailBody ?? '';
    return body
      .replace(/{officeName}/gi, office?.name ?? '')
      .replace(/\{officeName\}/gi, office?.name ?? '');
  }, [officeToUpdate?.cateringNotificationEmailBody, office?.name]);

  const fetchPreview = useCallback((daysAhead?: number): void => {
    if (officeId == null) return;
    setIsLoadingPreview(true);
    getCateringLunchPreview(officeId, keycloak, daysAhead)
      .then((rows) => { setPreviewRows(rows ?? []); })
      .catch(() => { setPreviewRows([]); })
      .finally(() => { setIsLoadingPreview(false); });
  }, [officeId, keycloak]);

  useEffect(() => {
    if (office != null) {
      setOfficeToUpdate((prev) => {
        if (prev == null || prev.id !== office.id) {
          // Switching to a different office: reset everything from the API response.
          return { ...office };
        }
        // Same office re-fetching (e.g. after OfficeInfoGroup saves the fill-in period):
        // Refresh all non-catering fields from the fresh API data so stale values (like
        // planPeriodDays) are not accidentally sent back to the server when catering is saved.
        // Catering-specific fields are kept as-is to preserve any unsaved user edits.
        return {
          ...office,
          cateringNotificationEnabled: prev.cateringNotificationEnabled,
          cateringNotificationEmailSubject: prev.cateringNotificationEmailSubject,
          cateringNotificationEmailBody: prev.cateringNotificationEmailBody,
          cateringNotificationRecipients: prev.cateringNotificationRecipients,
          cateringNotificationSendTime: prev.cateringNotificationSendTime,
          cateringNotificationTimeZone: prev.cateringNotificationTimeZone,
          cateringNotificationDaysAhead: prev.cateringNotificationDaysAhead,
        };
      });
      setIsLoading(false);
    }
  }, [office]);

  // When the Fill-in Period changes to a lower value, auto-clamp cateringNotificationDaysAhead
  // so it never exceeds the new max, and refresh the preview to reflect the adjusted range.
  useEffect(() => {
    setOfficeToUpdate((prev) => {
      if (prev == null) return prev;
      const current = prev.cateringNotificationDaysAhead ?? planPeriodDays;
      if (current <= planPeriodDays) return prev;
      fetchPreview(planPeriodDays);
      return { ...prev, cateringNotificationDaysAhead: planPeriodDays };
    });
  }, [planPeriodDays]);

  useEffect(() => {
    fetchPreview(undefined);
  }, [fetchPreview]);

  // Collapse the preview accordion whenever catering is disabled.
  useEffect(() => {
    if (!(officeToUpdate?.cateringNotificationEnabled ?? false)) {
      setIsPreviewExpanded(false);
    }
  }, [officeToUpdate?.cateringNotificationEnabled]);

  const handleChange = (formType: OfficeFormType, value: unknown): void => {
    if (officeToUpdate === undefined) return;
    const officeValidation = ApplyValueToOffice(formType, value, officeToUpdate);
    setOfficeToUpdate(officeValidation.office);
  };

  const saveOffice = useCallback(
    async (newOffice: Office): Promise<void> => {
      setIsSaving(true);
      const officeToSend = { ...newOffice };
      officeToSend.officeId = officeId ?? '';
      const officeResult = await updateOffice(officeToSend, keycloak);
      if (officeResult != null) {
        notify(t('catering-saved-success-message'), true);
        fetchPreview(newOffice.cateringNotificationDaysAhead);
      } else {
        notify(t('catering-save-error-message'), false);
      }
      setIsSaving(false);
    },
    [keycloak, officeId, t, fetchPreview],
  );

  if (isLoading) {
    return (
      <Center>
        <ActivityIndicator />
      </Center>
    );
  }

  const isEnabled = officeToUpdate?.cateringNotificationEnabled ?? false;

  return (
    <>
      {/* Page header */}
      <div style={{ marginTop: 8, marginBottom: 4 }}>
        <span style={{
          fontSize: 24,
          fontWeight: 600,
          fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
          color: StyleConstants.GREY_COLOR,
          display: 'block',
        }}
        >
          {t('catering-notifications')}
        </span>
        <p style={{
          marginTop: 4,
          marginBottom: 0,
          fontSize: StyleConstants.SECONDARY_FONT_SIZE,
          color: StyleConstants.GREY_COLOR,
          fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
        }}
        >
          {t('catering-description')}
        </p>
      </div>

      {/* Feature Settings card */}
      <div className={style(CommonStyles.sectionCard(isMobile))}>
        <Column crossAxisAlignment={CrossAxisAlignment.Stretch}>
          <span className={style(styles.columnHeader(isMobile, 8))}>
            {t('catering-feature-settings').toUpperCase()}
          </span>
          <div className={style(styles.horizontalDivider)} />

          {/* Toggle row */}
          <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={14}>
            <Switch
              checked={isEnabled}
              onChange={(checked): void => handleChange(OfficeFormType.cateringNotificationEnabled, checked)}
              onColor={StyleConstants.MEDIUM_BLUE_COLOR}
              onHandleColor={StyleConstants.WHITE_COLOR}
              handleDiameter={styles.switchDiameter}
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow={styles.switchBoxShadow}
              activeBoxShadow={styles.switchActiveBoxShadow}
              height={styles.switchHeight}
              width={styles.switchWidth}
            />
            <Column crossAxisAlignment={CrossAxisAlignment.Start}>
              <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={6}>
                <span style={{
                  fontSize: StyleConstants.SECONDARY_FONT_SIZE,
                  fontWeight: 600,
                  color: StyleConstants.GREY_COLOR,
                }}
                >
                  {t('enable-catering-notifications')}
                </span>
                <InfoOutlinedIcon
                  data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
                  data-tooltip-content={t('catering-enable-tooltip').toString()}
                  sx={styles.tooltipIconSX}
                />
              </Row>
              <span style={{
                fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                color: StyleConstants.GREY_COLOR,
                opacity: 0.8,
              }}
              >
                {t('enable-catering-description')}
              </span>
            </Column>
          </Row>

          {/* Fields — dim when disabled */}
          <div style={{ opacity: isEnabled ? 1 : 0.45, marginTop: 16, pointerEvents: isEnabled ? 'auto' : 'none' }}>

            {/* Email subject */}
            <div style={{ marginBottom: 14 }}>
              <div className={style(styles.label)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {t('catering-email-subject')}
                <InfoOutlinedIcon
                  data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
                  data-tooltip-content={t('catering-email-subject-tooltip').toString()}
                  sx={styles.tooltipIconSX}
                />
              </div>
              <input
                style={{
                  width: '100%',
                  padding: 8,
                  boxSizing: 'border-box',
                  borderRadius: 6,
                  border: '1px solid rgba(36,55,75,0.25)',
                  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                  fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                  marginTop: 4,
                }}
                type="text"
                disabled={!isEnabled}
                value={officeToUpdate?.cateringNotificationEmailSubject ?? ''}
                onChange={(e): void => handleChange(OfficeFormType.cateringNotificationEmailSubject, e.target.value)}
                placeholder={t('catering-email-subject-placeholder').toString()}
              />
              <span style={{ fontSize: 11, color: StyleConstants.GREY_COLOR, opacity: 0.75 }}>
                {t('catering-email-subject-hint')}
              </span>
            </div>

            {/* Email message */}
            <div style={{ marginBottom: 14 }}>
              <div className={style(styles.label)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {t('catering-email-body')}
                <InfoOutlinedIcon
                  data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
                  data-tooltip-content={t('catering-email-body-tooltip').toString()}
                  sx={styles.tooltipIconSX}
                />
              </div>
              <textarea
                style={{
                  width: '100%',
                  minHeight: 90,
                  padding: 8,
                  boxSizing: 'border-box',
                  borderRadius: 6,
                  border: '1px solid rgba(36,55,75,0.25)',
                  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                  fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                  resize: 'vertical',
                  marginTop: 4,
                }}
                disabled={!isEnabled}
                value={officeToUpdate?.cateringNotificationEmailBody ?? ''}
                onChange={(e): void => handleChange(OfficeFormType.cateringNotificationEmailBody, e.target.value)}
                rows={4}
              />
              <span style={{
                fontSize: 11,
                color: StyleConstants.GREY_COLOR,
                opacity: 0.75,
              }}
              >
                {t('catering-email-body-hint')}
              </span>
            </div>

            {/* Recipients | Days ahead — side-by-side row */}
            <Row itemSpacing={24} crossAxisAlignment={CrossAxisAlignment.Start}>

              {/* Recipients */}
              <div style={{ flex: 1 }}>
                <div className={style(styles.label)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {t('catering-recipients')}
                  <InfoOutlinedIcon
                    data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
                    data-tooltip-content={t('catering-recipients-tooltip').toString()}
                    sx={styles.tooltipIconSX}
                  />
                </div>
                <input
                  style={{
                    width: '100%',
                    padding: 8,
                    boxSizing: 'border-box',
                    borderRadius: 6,
                    border: '1px solid rgba(36,55,75,0.25)',
                    fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                    fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                    marginTop: 4,
                  }}
                  type="text"
                  disabled={!isEnabled}
                  value={officeToUpdate?.cateringNotificationRecipients ?? ''}
                  onChange={(e): void => handleChange(OfficeFormType.cateringNotificationRecipients, e.target.value)}
                />
                <span style={{ fontSize: 11, color: StyleConstants.GREY_COLOR, opacity: 0.75 }}>
                  {t('catering-recipients-hint')}
                </span>
              </div>

              {/* Days ahead to include */}
              <div style={{ flex: 1 }}>
                <div className={style(styles.label)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {t('catering-days-ahead')}
                  <InfoOutlinedIcon
                    data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
                    data-tooltip-content={t('catering-days-ahead-tooltip').toString()}
                    sx={styles.tooltipIconSX}
                  />
                </div>
                <input
                  style={{
                    width: '100%',
                    padding: 8,
                    boxSizing: 'border-box',
                    borderRadius: 6,
                    border: '1px solid rgba(36,55,75,0.25)',
                    fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                    fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                    backgroundColor: isEnabled ? '#fff' : 'rgba(0,0,0,0.03)',
                    marginTop: 4,
                  }}
                  type="number"
                  min={1}
                  max={planPeriodDays}
                  disabled={!isEnabled}
                  value={officeToUpdate?.cateringNotificationDaysAhead ?? planPeriodDays}
                  onChange={(e): void => {
                    const raw = parseInt(e.target.value, 10);
                    const clamped = Number.isNaN(raw) ? planPeriodDays : Math.min(Math.max(1, raw), planPeriodDays);
                    handleChange(OfficeFormType.cateringNotificationDaysAhead, clamped);
                  }}
                />
                <span style={{ fontSize: 11, color: StyleConstants.GREY_COLOR, opacity: 0.75 }}>
                  {t('catering-days-ahead-hint')}
                </span>
              </div>
            </Row>

            {/* ── Send Schedule ── */}
            <div style={{ marginTop: 16 }}>
              {/* Sub-section label */}
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: StyleConstants.GREY_COLOR,
                opacity: 0.55,
              }}
              >
                {t('catering-send-schedule')}
              </span>
              <div className={style(styles.horizontalDivider)} style={{ marginTop: 6, marginBottom: 14 }} />

              {/* Two-column row: Send time (left) | Recipient time zone (right) */}
              <Row itemSpacing={24} crossAxisAlignment={CrossAxisAlignment.Start}>

                {/* ── Left: Send time ── */}
                <div style={{ flex: 1 }}>
                  <div
                    className={style(styles.label)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {t('catering-send-time')}
                    <InfoOutlinedIcon
                      data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
                      data-tooltip-content={t('catering-send-time-tooltip').toString()}
                      sx={styles.tooltipIconSX}
                    />
                  </div>

                  <div style={{ marginTop: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimePicker
                        ampm={false} // forces 24-hour format
                        disabled={!isEnabled}
                        value={
                          officeToUpdate?.cateringNotificationSendTime
                            ? parse(
                              officeToUpdate.cateringNotificationSendTime,
                              'HH:mm',
                              new Date()
                            )
                            : null
                        }
                        onChange={(newValue): void => {
                          if (newValue == null) {
                            handleChange(OfficeFormType.cateringNotificationSendTime, '');
                          } else if (isValid(newValue)) {
                            handleChange(OfficeFormType.cateringNotificationSendTime, format(newValue, 'HH:mm'));
                          }
                          // Partial / invalid input while user is still typing — skip update
                        }}
                        renderInput={(params): React.JSX.Element => (
                          <TextField
                            {...params}
                            sx={{
                              '& .MuiInputBase-root': {
                                marginTop: 0,
                                padding: '2px 8px',
                                borderRadius: '6px !important',
                                border: '1px solid rgba(36,55,75,0.25)',
                                fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                                fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                                backgroundColor: isEnabled ? '#fff' : 'rgba(0,0,0,0.03)',
                                cursor: isEnabled ? 'pointer' : 'not-allowed',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                              },
                              '& input': {
                                padding: '6px 0',
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </div>

                  <span style={{ fontSize: 11, color: StyleConstants.GREY_COLOR, opacity: 0.75 }}>
                    {t('catering-send-time-hint')}
                  </span>
                </div>

                {/* ── Right: Recipient time zone ── */}
                <div style={{ flex: 1 }}>
                  <div className={style(styles.label)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {t('catering-timezone')}
                    <InfoOutlinedIcon
                      data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
                      data-tooltip-content={t('catering-timezone-tooltip').toString()}
                      sx={styles.tooltipIconSX}
                    />
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <TimezoneSelector
                      value={officeToUpdate?.cateringNotificationTimeZone ?? 'UTC'}
                      onChange={(tz): void => handleChange(OfficeFormType.cateringNotificationTimeZone, tz)}
                      disabled={!isEnabled}
                      searchPlaceholder={t('catering-timezone-search').toString()}
                    />
                  </div>
                  <span style={{ fontSize: 11, color: StyleConstants.GREY_COLOR, opacity: 0.75 }}>
                    {t('catering-timezone-hint')}
                  </span>
                </div>
              </Row>

              {/* "Scheduled at X UTC" pill — live, plain text weight (no bold). */}
              {((): React.JSX.Element | null => {
                const tz = officeToUpdate?.cateringNotificationTimeZone ?? 'UTC';
                const localTime = officeToUpdate?.cateringNotificationSendTime ?? '';
                const utcTime = localToUtcHHmm(localTime, tz);
                if (utcTime === '') return null;
                return (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: 12,
                    padding: '5px 12px',
                    borderRadius: 20,
                    border: '1px solid rgba(36,55,75,0.18)',
                    backgroundColor: 'rgba(36,55,75,0.04)',
                    fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                    fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                    color: StyleConstants.GREY_COLOR,
                    width: 'fit-content',
                  }}
                  >
                    <AccessTimeIcon style={{ fontSize: 15, opacity: 0.6 }} />
                    <span>{t('catering-scheduled-at')} {utcTime} UTC</span>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className={style(styles.horizontalDivider)} style={{ marginTop: 20 }} />

          {/* Buttons */}
          <Row itemSpacing={12}>
            <PrimaryButton
              loading={isSaving}
              onClick={(): void => { saveOffice(officeToUpdate ?? {} as Office).catch(() => undefined); }}
            >
              {t('catering-save-settings')}
            </PrimaryButton>
            <button
              type="button"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 32,
                width: 180,
                borderRadius: 8,
                border: `1px solid ${StyleConstants.GREY_COLOR}`,
                fontSize: StyleConstants.PRIMARY_BUTTON_FONT_SIZE,
                fontWeight: StyleConstants.PRIMARY_BUTTON_FONT_WEIGHT,
                color: StyleConstants.GREY_COLOR,
                cursor: isSendingTestEmail || officeId == null || !isEnabled ? 'not-allowed' : 'pointer',
                opacity: !isEnabled ? 0.45 : 1,
                userSelect: 'none',
                boxSizing: 'border-box',
                fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                background: 'transparent',
              }}
              disabled={isSendingTestEmail || officeId == null || !isEnabled}
              onClick={(): void => {
                if (officeId == null) return;
                setIsSendingTestEmail(true);
                sendCateringTestEmail(officeId, keycloak)
                  .then((success) => {
                    if (success) {
                      notify(t('catering-test-email-success'), true);
                    } else {
                      notify(t('catering-test-email-error'), false);
                    }
                  })
                  .catch(() => { notify(t('catering-test-email-error'), false); })
                  .finally(() => { setIsSendingTestEmail(false); });
              }}
            >
              {t('catering-send-test-email')}
            </button>
          </Row>
        </Column>
      </div>

      {/* Email Preview card */}
      <div className={style(CommonStyles.sectionCard(isMobile))}>
        <Column crossAxisAlignment={CrossAxisAlignment.Stretch}>
          <button
            type="button"
            onClick={(): void => { if (isEnabled) setIsPreviewExpanded((prev) => !prev); }}
            disabled={!isEnabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: isEnabled ? 'pointer' : 'not-allowed',
              width: '100%',
              textAlign: 'left',
              opacity: isEnabled ? 1 : 0.45,
            }}
          >
            <span className={style(styles.columnHeader(isMobile, 8))}>
              {t('catering-email-preview').toUpperCase()}
            </span>
            <ExpandMoreIcon
              sx={{
                color: StyleConstants.GREY_COLOR,
                fontSize: 22,
                transition: 'transform 0.2s ease',
                transform: isPreviewExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </button>

          {isPreviewExpanded && (
            <>
              <div className={style(styles.horizontalDivider)} />

              <div style={{
                border: '1px dashed rgba(36,55,75,0.3)',
                borderRadius: 8,
                padding: 20,
                backgroundColor: '#fafafa',
                fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                color: StyleConstants.GREY_COLOR,
              }}
              >
                {/* Subject line preview */}
                <p style={{ margin: '0 0 12px 0', fontWeight: 600, fontSize: StyleConstants.SECONDARY_FONT_SIZE }}>
                  {officeToUpdate?.cateringNotificationEmailSubject?.replace('{officeName}', office?.name ?? '')
                    || `Catering Notification for office ${office?.name ?? '...'}`}
                </p>

                {previewEmailBody.length > 0 && (
                  <p style={{ margin: '0 0 12px 0', whiteSpace: 'pre-wrap' }}>{previewEmailBody}</p>
                )}

                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#FFB63E' }}>
                      <th style={{ padding: '6px 12px', textAlign: 'left', color: StyleConstants.WHITE_COLOR, fontWeight: 600 }}>Datum</th>
                      <th style={{ padding: '6px 12px', textAlign: 'left', color: StyleConstants.WHITE_COLOR, fontWeight: 600 }}>Dag</th>
                      <th style={{ padding: '6px 12px', textAlign: 'left', color: StyleConstants.WHITE_COLOR, fontWeight: 600 }}>Lunch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingPreview && (
                      <tr>
                        <td colSpan={3} style={{ padding: '12px', textAlign: 'center', opacity: 0.6 }}>
                          <ActivityIndicator />
                        </td>
                      </tr>
                    )}
                    {!isLoadingPreview && previewRows.length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ padding: '10px 12px', opacity: 0.6, fontStyle: 'italic' }}>
                          {t('catering-no-lunch-data')}
                        </td>
                      </tr>
                    )}
                    {!isLoadingPreview && previewRows.map((row) => (
                      <tr key={row.datum}>
                        <td style={{ padding: '5px 12px', borderBottom: '1px solid rgba(36,55,75,0.1)' }}>{row.datum}</td>
                        <td style={{ padding: '5px 12px', borderBottom: '1px solid rgba(36,55,75,0.1)' }}>{row.dag}</td>
                        <td style={{ padding: '5px 12px', borderBottom: '1px solid rgba(36,55,75,0.1)' }}>{row.lunch}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Column>
      </div>
    </>
  );
};

export default OfficeCateringGroup;
